function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function stripErrorCodePrefix(message: string): string {
  const patterns = [
    /^\[[A-Z0-9_.-]+\]\s*/u,
    /^\d{3}\s+[A-Z][A-Z0-9_ ]*\s*[:,-]\s*/u,
    /^\d{3}\s*[:,-]\s*/u,
    /^[A-Z][A-Z0-9_.-]*\s*[:,-]\s*/u,
  ]

  let normalized = message.trim()
  let didChange = true

  while (didChange) {
    didChange = false

    for (const pattern of patterns) {
      const nextValue = normalized.replace(pattern, '').trim()
      if (nextValue !== normalized) {
        normalized = nextValue
        didChange = true
      }
    }
  }

  return normalized
}

function stringifyBackendPayload(value: unknown, stripCodes = false): string | null {
  if (typeof value === 'string') {
    const normalized = stripCodes ? stripErrorCodePrefix(value) : value.trim()
    return normalized || null
  }

  if (Array.isArray(value)) {
    const parts = value
      .map((item) => stringifyBackendPayload(item, stripCodes))
      .filter((item): item is string => Boolean(item))
    return parts.length > 0 ? parts.join('\n') : null
  }

  if (!isRecord(value)) return null

  for (const key of ['message', 'detail', 'error']) {
    const nested = stringifyBackendPayload(value[key], stripCodes)
    if (nested) return nested
  }

  const nonFieldErrors = stringifyBackendPayload(value.non_field_errors, stripCodes)
  if (nonFieldErrors) return nonFieldErrors

  const fieldMessages = Object.entries(value)
    .map(([key, fieldValue]) => {
      if (stripCodes && key === 'code') return null

      const nested = stringifyBackendPayload(fieldValue, stripCodes)
      if (!nested) return null
      return Array.isArray(fieldValue) ? `${key}: ${nested}` : nested
    })
    .filter((item): item is string => Boolean(item))

  if (fieldMessages.length > 0) return fieldMessages.join('\n')

  try {
    return JSON.stringify(value)
  } catch {
    return null
  }
}

export function isApiError(error: unknown): error is { status: number; data?: unknown } {
  return isRecord(error) && typeof error.status === 'number'
}

export function getErrorMessage(error: unknown, fallback = 'Произошла ошибка'): string {
  if (isApiError(error) && 'data' in error) {
    const message = stringifyBackendPayload(error.data)
    return message ?? fallback
  }

  if (error instanceof Error) {
    const message = error.message.trim()
    return message || fallback
  }

  return fallback
}

export function getToastErrorMessage(error: unknown, fallback = 'Произошла ошибка'): string {
  if (isApiError(error) && 'data' in error) {
    const message = stringifyBackendPayload(error.data, true)
    return message ?? fallback
  }

  if (error instanceof Error) {
    const message = stripErrorCodePrefix(error.message)
    return message || fallback
  }

  return fallback
}
